import {
  useRoute,
  useDateFormatter,
  type QueryToRoute,
} from '~/infrastructure';
import { useRouter } from 'vue-router';
import { computed, type ComputedRef, type Ref } from 'vue';
import { type ProductFilters } from '~/domains/product';
import { isObject } from '~/utils';

interface ReturnUseAddQueryParams {
  removeQueryParams: () => void;
  updateFiltersQueryToRoute: () => Promise<void>;
  addPageQueryParamToRoute: (page: number) => Promise<void>;
  addSortQueryParamToRoute: (sort?: string) => Promise<void>;
  filterQueryParamsForRoute: ComputedRef<FilterQueryParams>;
  addFiltersQueryParamsToRoute: (filters: QueryToRoute) => Promise<void>;
}

interface FilterQueryParams {
  parentId?: string;
  stock?: string;
  price?: string;
  brands?: string;
  productTypes?: string;
  nodes?: string;
  subnodes?: string;
  cableLength?: string;
  keyAttributes?: string;
  search?: string;
}

export default (filters?: Ref<ProductFilters>): ReturnUseAddQueryParams => {
  const route = useRoute();
  const router = useRouter();
  const { getIsoDateStringWithoutTime } = useDateFormatter();

  const removeQueryParams = (): void => {
    const { href } = router.resolve({
      path: route.path,
      query: {},
    });

    history.replaceState({}, '', href);
  };

  const isNotEmptyFilter = (value: any): boolean => {
    if (Array.isArray(value)) {
      return value.length !== 0;
    } else if (isObject(value)) {
      return Object.keys(value as keyof typeof filters).some((key) =>
        isNotEmptyFilter(value[key]),
      );
    } else {
      return value != null;
    }
  };

  const addPageQueryParamToRoute = async (page: number): Promise<void> => {
    await router.push({
      query: { ...route.query, page: page === 1 ? undefined : page },
    });
  };

  const addSortQueryParamToRoute = async (sort?: string): Promise<void> => {
    await router.push({
      query: { ...route.query, sort },
    });
  };

  const updateFiltersQueryToRoute = async (): Promise<void> => {
    const queryRouteParams = {
      ...(Object.keys(filterQueryParamsForRoute.value).length !== 0 &&
        filterQueryParamsForRoute.value),
    };

    // TODO: refactor
    for (const key in queryRouteParams) {
      if (
        queryRouteParams[key] === 'false' ||
        queryRouteParams[key] === false
      ) {
        queryRouteParams[key] = undefined;
      }
    }
    // END TODO

    await router.push({
      query: { ...route.query, ...queryRouteParams, page: undefined },
    });
  };

  const filterQueryParamsForRoute = computed(() => {
    if (filters?.value === undefined) return { ...route.query };

    const result: FilterQueryParams = Object.fromEntries(
      Object.entries(filters.value).map(([key, _]) => {
        return [key, undefined];
      }),
    );
    const parentIds: string[] = [];

    Object.entries(filters.value).forEach(([key, value]) => {
      if (key !== 'stock') {
        if (!isNotEmptyFilter(value)) return;

        if (Array.isArray(value)) {
          result[key as keyof FilterQueryParams] = value
            .map((filter) => {
              if (isObject(filter) && filter.parentId !== undefined) {
                parentIds.push(filter.parentId as string);
              }
              return isObject(filter) ? filter.id : filter;
            })
            .join(',');
        } else {
          result[key as keyof FilterQueryParams] = isObject(value)
            ? value.id
            : value;
        }
      }
    });

    parentIds.length > 0
      ? (result.parentId = parentIds.join(','))
      : (result.parentId = undefined);

    return result;
  });

  const addFiltersQueryParamsToRoute = async (
    filters: QueryToRoute,
  ): Promise<void> => {
    const filteredFilters = Object.fromEntries(
      Object.entries(filters).filter(([key, value]) => {
        if (key === 'open' && value != null) return true;
        else return value;
      }),
    );

    const dateFrom =
      filteredFilters.dateFrom !== undefined
        ? getIsoDateStringWithoutTime(filteredFilters.dateFrom as string)
        : undefined;

    const dateTill =
      filteredFilters.dateTill !== undefined
        ? getIsoDateStringWithoutTime(filteredFilters.dateTill as string)
        : undefined;

    const queryRouteParams = {
      ...(Object.keys(filteredFilters).length !== 0 && filteredFilters),
      dateFrom,
      dateTill,
    };

    await router.push({
      query: { ...queryRouteParams, page: undefined },
    });
  };

  return {
    removeQueryParams,
    updateFiltersQueryToRoute,
    addPageQueryParamToRoute,
    filterQueryParamsForRoute,
    addFiltersQueryParamsToRoute,
    addSortQueryParamToRoute,
  };
};
